import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Plus, Trash2, Save, Package, Users, MessageSquare, TrendingUp, Sparkles, LogOut } from "lucide-react";

const TABS = [
    { id: "dashboard", label: "Dashboard", icon: TrendingUp },
    { id: "products", label: "Products", icon: Package },
    { id: "rates", label: "Gold Rates", icon: Sparkles },
    { id: "reels", label: "Reels", icon: MessageSquare },
    { id: "enquiries", label: "Enquiries", icon: MessageSquare },
    { id: "settings", label: "Settings", icon: Users },
];

export default function Admin() {
    const { user, loading, logout } = useAuth();
    const { rates, settings, refresh } = useSettings();
    const [tab, setTab] = useState("dashboard");
    const [stats, setStats] = useState({});
    const [products, setProducts] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [reels, setReels] = useState([]);
    const [newReel, setNewReel] = useState({ title: "", url: "", thumbnail: "", caption: "" });
    const [rateForm, setRateForm] = useState({});
    const [settingsForm, setSettingsForm] = useState({});
    const [newProduct, setNewProduct] = useState({
        name: "", category: "gold", subcategory: "rings", collection: "Contemporary",
        metal: "Gold", purity: "22K", gross_weight: 0, price: 0, description: "",
        images: [], featured: false, new_arrival: false, access_level: "public",
        price_visibility: "visible", availability: "in_stock",
    });

    useEffect(() => {
        if (user?.role === "admin") {
            client.get("/admin/stats").then(r => setStats(r.data));
            client.get("/products?limit=200").then(r => setProducts(r.data));
            client.get("/enquiries").then(r => setEnquiries(r.data));
            client.get("/reels").then(r => setReels(r.data));
        }
    }, [user]);

    useEffect(() => { setRateForm(rates); }, [rates]);
    useEffect(() => { setSettingsForm(settings); }, [settings]);

    if (loading) return null;
    if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

    const saveRates = async () => {
        try {
            await client.put("/rates", {
                gold_24k: +rateForm.gold_24k, gold_22k: +rateForm.gold_22k,
                gold_20k: +rateForm.gold_20k, gold_18k: +rateForm.gold_18k,
                silver: +rateForm.silver,
            });
            toast.success("Rates updated");
            refresh();
        } catch { toast.error("Failed"); }
    };

    const saveSettings = async () => {
        try {
            await client.put("/settings", settingsForm);
            toast.success("Settings updated");
            refresh();
        } catch { toast.error("Failed"); }
    };

    const addProduct = async () => {
        try {
            const imgs = newProduct.images.filter(Boolean);
            if (imgs.length === 0) return toast.error("Add at least one photo");
            const payload = { ...newProduct, gross_weight: +newProduct.gross_weight, price: +newProduct.price, images: imgs };
            const { data } = await client.post("/products", payload);
            setProducts([data, ...products]);
            toast.success("Product added");
            setNewProduct({ ...newProduct, name: "", images: [] });
        } catch { toast.error("Failed"); }
    };

    const uploadPhotos = async (files) => {
        const readers = [...files].map(f => new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.onerror = rej;
            r.readAsDataURL(f);
        }));
        try {
            const results = await Promise.all(readers);
            setNewProduct(np => ({ ...np, images: [...(np.images || []).filter(Boolean), ...results] }));
            toast.success(`${results.length} photo${results.length > 1 ? "s" : ""} added`);
        } catch { toast.error("Upload failed"); }
    };

    const removeImg = (idx) => {
        setNewProduct(np => ({ ...np, images: np.images.filter((_, i) => i !== idx) }));
    };

    const addReel = async () => {
        if (!newReel.title || !newReel.url || !newReel.thumbnail) return toast.error("Title, URL and thumbnail are required");
        try {
            const { data } = await client.post("/reels", newReel);
            setReels([data, ...reels]);
            setNewReel({ title: "", url: "", thumbnail: "", caption: "" });
            toast.success("Reel added");
        } catch { toast.error("Failed"); }
    };

    const uploadReelThumb = async (file) => {
        if (!file) return;
        const r = new FileReader();
        r.onload = () => setNewReel(nr => ({ ...nr, thumbnail: r.result }));
        r.readAsDataURL(file);
    };

    const delReel = async (id) => {
        if (!window.confirm("Delete this reel?")) return;
        await client.delete(`/reels/${id}`);
        setReels(reels.filter(r => r.id !== id));
    };

    const delProduct = async (id) => {
        if (!window.confirm("Delete this product?")) return;
        await client.delete(`/products/${id}`);
        setProducts(products.filter(p => p.id !== id));
        toast.success("Deleted");
    };

    return (
        <div className="max-w-[1440px] mx-auto px-6 py-10">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <div className="eyebrow">Admin</div>
                    <h1 className="font-serif-luxe text-4xl">Singh Jewellers Console</h1>
                </div>
                <button onClick={logout} className="btn-outline"><LogOut size={14} /> Logout</button>
            </div>

            <div className="grid lg:grid-cols-[220px_1fr] gap-8">
                <aside className="space-y-1">
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 text-sm border-l-2 ${tab === t.id ? "border-[color:var(--champagne)] bg-[color:var(--beige)]/40" : "border-transparent"}`}>
                            <t.icon size={16} /> {t.label}
                        </button>
                    ))}
                </aside>

                <div>
                    {tab === "dashboard" && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { l: "Total Products", v: stats.total_products },
                                { l: "Gold", v: stats.gold },
                                { l: "Silver", v: stats.silver },
                                { l: "Diamond", v: stats.diamond },
                                { l: "Bridal", v: stats.bridal },
                                { l: "New Arrivals", v: stats.new_arrivals },
                                { l: "Customers", v: stats.customers },
                                { l: "New Enquiries", v: stats.new_enquiries },
                            ].map(s => (
                                <div key={s.l} className="card-luxe p-6">
                                    <div className="eyebrow text-[color:var(--muted-brown)]">{s.l}</div>
                                    <div className="font-serif-luxe text-4xl mt-2 text-[color:var(--charcoal)]">{s.v ?? 0}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "products" && (
                        <div>
                            <div className="card-luxe p-6 mb-8">
                                <div className="font-serif-luxe text-xl mb-4">Add New Product</div>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <input placeholder="Name" value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} data-testid="np-name" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <select value={newProduct.category} onChange={e => setNewProduct({...newProduct, category: e.target.value})} data-testid="np-category" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent">
                                        {["gold","silver","diamond","bridal","everyday"].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <input placeholder="Subcategory" value={newProduct.subcategory} onChange={e => setNewProduct({...newProduct, subcategory: e.target.value})} className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input placeholder="Collection" value={newProduct.collection} onChange={e => setNewProduct({...newProduct, collection: e.target.value})} className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input placeholder="Purity (22K)" value={newProduct.purity} onChange={e => setNewProduct({...newProduct, purity: e.target.value})} className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input type="number" placeholder="Weight (g)" value={newProduct.gross_weight} onChange={e => setNewProduct({...newProduct, gross_weight: e.target.value})} className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input type="number" placeholder="Price (₹)" value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} data-testid="np-price" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <select value={newProduct.access_level} onChange={e => setNewProduct({...newProduct, access_level: e.target.value})} className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent">
                                        <option value="public">Public</option>
                                        <option value="vip">VIP / Exclusive</option>
                                    </select>
                                    <input placeholder="Image URL (or use upload below)" value={newProduct.images[0] || ""} onChange={e => setNewProduct({...newProduct, images: [e.target.value, ...newProduct.images.slice(1)]})} data-testid="np-image" className="md:col-span-2 border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <div className="md:col-span-2">
                                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)] block mb-1.5">Upload real photos (multiple)</label>
                                        <input type="file" accept="image/*" multiple onChange={(e) => uploadPhotos(e.target.files)} data-testid="np-upload" className="w-full text-xs" />
                                        {newProduct.images.filter(Boolean).length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                {newProduct.images.filter(Boolean).map((img, i) => (
                                                    <div key={i} className="relative w-16 h-16 border border-[color:var(--soft-border)]">
                                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                                        <button type="button" onClick={() => removeImg(i)} className="absolute -top-1.5 -right-1.5 bg-[color:var(--destructive)] text-white rounded-full w-5 h-5 text-xs">×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <textarea placeholder="Description" value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} rows={3} className="md:col-span-2 border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newProduct.featured} onChange={e => setNewProduct({...newProduct, featured: e.target.checked})} /> Featured</label>
                                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={newProduct.new_arrival} onChange={e => setNewProduct({...newProduct, new_arrival: e.target.checked})} /> New Arrival</label>
                                </div>
                                <button onClick={addProduct} className="btn-primary mt-4" data-testid="np-add"><Plus size={14} /> Add Product</button>
                            </div>

                            <div className="font-serif-luxe text-xl mb-4">All Products ({products.length})</div>
                            <div className="space-y-2 max-h-[600px] overflow-y-auto">
                                {products.map(p => (
                                    <div key={p.id} className="card-luxe p-3 flex items-center gap-4">
                                        <img src={p.images?.[0]} alt="" className="w-14 h-14 object-cover" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-serif-luxe text-sm truncate">{p.name}</div>
                                            <div className="text-xs text-[color:var(--muted-brown)]">{p.sku} · {p.category} · ₹{p.price?.toLocaleString("en-IN")}</div>
                                        </div>
                                        <button onClick={() => delProduct(p.id)} className="text-[color:var(--destructive)]" data-testid={`del-${p.id}`}><Trash2 size={16} /></button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "rates" && (
                        <div className="card-luxe p-8">
                            <div className="font-serif-luxe text-2xl mb-6">Update Gold & Silver Rates</div>
                            <div className="grid md:grid-cols-2 gap-4">
                                {["gold_24k","gold_22k","gold_20k","gold_18k","silver"].map(k => (
                                    <div key={k}>
                                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">{k.replace("_", " ")}</label>
                                        <input type="number" value={rateForm[k] || ""} onChange={e => setRateForm({...rateForm, [k]: e.target.value})} data-testid={`rate-${k}`} className="w-full border border-[color:var(--soft-border)] px-3 py-2 mt-1 bg-transparent" />
                                    </div>
                                ))}
                            </div>
                            <button onClick={saveRates} className="btn-primary mt-6" data-testid="save-rates"><Save size={14} /> Save Rates</button>
                        </div>
                    )}

                    {tab === "reels" && (
                        <div>
                            <div className="card-luxe p-6 mb-8">
                                <div className="font-serif-luxe text-xl mb-2">Add New Reel</div>
                                <p className="text-xs text-[color:var(--muted-brown)] mb-4">Paste your Instagram reel URL and either upload a thumbnail or paste an image URL. Reels appear in the "Reels from the Showroom" section on the homepage.</p>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <input placeholder="Title (e.g. Bridal Look)" value={newReel.title} onChange={e => setNewReel({...newReel, title: e.target.value})} data-testid="reel-title" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input placeholder="Instagram URL" value={newReel.url} onChange={e => setNewReel({...newReel, url: e.target.value})} data-testid="reel-url" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input placeholder="Thumbnail URL (or upload below)" value={newReel.thumbnail?.startsWith("data:") ? "[uploaded]" : newReel.thumbnail} onChange={e => setNewReel({...newReel, thumbnail: e.target.value})} data-testid="reel-thumb-url" className="border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    <input type="file" accept="image/*" onChange={(e) => uploadReelThumb(e.target.files[0])} data-testid="reel-upload" className="text-xs pt-2" />
                                    <input placeholder="Caption (optional)" value={newReel.caption} onChange={e => setNewReel({...newReel, caption: e.target.value})} className="md:col-span-2 border border-[color:var(--soft-border)] px-3 py-2 text-sm bg-transparent" />
                                    {newReel.thumbnail && (
                                        <div className="md:col-span-2">
                                            <div className="text-xs eyebrow mb-1">Preview</div>
                                            <img src={newReel.thumbnail} alt="preview" className="w-24 h-40 object-cover border border-[color:var(--soft-border)]" />
                                        </div>
                                    )}
                                </div>
                                <button onClick={addReel} className="btn-primary mt-4" data-testid="reel-add"><Plus size={14} /> Add Reel</button>
                            </div>

                            <div className="font-serif-luxe text-xl mb-4">All Reels ({reels.length})</div>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                {reels.map(r => (
                                    <div key={r.id} className="card-luxe overflow-hidden relative">
                                        <div className="aspect-[9/16] bg-[color:var(--beige)]">
                                            <img src={r.thumbnail} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="p-2">
                                            <div className="text-xs font-serif-luxe truncate">{r.title}</div>
                                            <div className="flex items-center justify-between mt-1">
                                                <a href={r.url} target="_blank" rel="noreferrer" className="text-[10px] tracking-widest text-[color:var(--champagne)]">VIEW</a>
                                                <button onClick={() => delReel(r.id)} className="text-[color:var(--destructive)]" data-testid={`del-reel-${r.id}`}><Trash2 size={12} /></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "enquiries" && (
                        <div className="space-y-3">
                            <div className="font-serif-luxe text-2xl mb-2">Customer Enquiries</div>
                            {enquiries.length === 0 && <div className="text-sm text-[color:var(--muted-brown)]">No enquiries yet.</div>}
                            {enquiries.map(e => (
                                <div key={e.id} className="card-luxe p-5">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <div className="font-serif-luxe text-lg">{e.name} <span className="text-xs eyebrow ml-2 text-[color:var(--champagne)]">{e.type}</span></div>
                                            <div className="text-xs text-[color:var(--muted-brown)]">{e.phone} · {e.email}</div>
                                        </div>
                                        <div className="text-xs">{new Date(e.created_at).toLocaleDateString()}</div>
                                    </div>
                                    {e.product_name && <div className="text-xs text-[color:var(--muted-brown)]">Product: {e.product_name}</div>}
                                    {e.message && <p className="text-sm mt-2">{e.message}</p>}
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "settings" && (
                        <div className="card-luxe p-8 space-y-3">
                            <div className="font-serif-luxe text-2xl mb-4">Site Settings</div>
                            {["announcement", "whatsapp_number", "phone", "email", "address", "hours", "hero_headline", "hero_subheadline"].map(k => (
                                <div key={k}>
                                    <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">{k.replace(/_/g, " ")}</label>
                                    <input value={settingsForm[k] || ""} onChange={e => setSettingsForm({...settingsForm, [k]: e.target.value})} data-testid={`setting-${k}`} className="w-full border border-[color:var(--soft-border)] px-3 py-2 mt-1 bg-transparent" />
                                </div>
                            ))}
                            <button onClick={saveSettings} className="btn-primary mt-2" data-testid="save-settings"><Save size={14} /> Save Settings</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
